<script setup lang="ts">
// App-wide single-line prompt dialog. Mounted once in AppShell; views
// call `usePromptStore().ask({...})`. Uses the shared Modal shell so it
// reads as one system with ConfirmModal et al.
//
// Keyboard:
//   * Enter   -> submit (when valid)
//   * Escape  -> cancel (Modal closes; @close = cancel)
// The input autofocuses and selects its initial value each time a new
// request opens, so the common "rename / accept the suggested name"
// flow is a single keystroke.

import { computed, nextTick, ref, watch } from 'vue';
import Modal from './Modal.vue';

import { usePromptStore } from '@/stores/prompt';
import { useI18n } from '@/lib/i18n';

const { t } = useI18n();
const store = usePromptStore();
const open = computed(() => store.pending !== null);
const req  = computed(() => store.pending);

const input = ref<HTMLInputElement | null>(null);
const value = ref('');
const error = ref<string | null>(null);

const confirmLabel = computed(() => req.value?.confirmLabel ?? t('prompt.ok'));
const cancelLabel  = computed(() => req.value?.cancelLabel ?? t('common.cancel'));

watch(open, async (isOpen) => {
  if (!isOpen) return;
  value.value = req.value?.initialValue ?? '';
  error.value = null;
  await nextTick();
  input.value?.focus();
  input.value?.select();
});

function submit(): void {
  const trimmed = value.value.trim();
  const err = req.value?.validate?.(trimmed) ?? null;
  if (err) { error.value = err; return; }
  store.resolveAndClose(trimmed);
}
function cancel(): void { store.resolveAndClose(null); }
</script>

<template>
  <Modal :open="open" :title="req?.title" :icon="'pencil-simple'" @close="cancel">
    <p v-if="req?.body" class="body">{{ req.body }}</p>

    <label class="field">
      <span v-if="req?.label" class="field__label">{{ req.label }}</span>
      <input
        ref="input"
        v-model="value"
        type="text"
        class="field__input"
        autocomplete="off"
        :placeholder="req?.placeholder ?? ''"
        :aria-invalid="error ? 'true' : undefined"
        @input="error = null"
        @keydown.enter.prevent="submit"
      />
      <span v-if="error" class="field__error">{{ error }}</span>
    </label>

    <template #footer>
      <button type="button" class="btn btn-secondary" @click="cancel">{{ cancelLabel }}</button>
      <button type="button" class="btn btn-primary" @click="submit">{{ confirmLabel }}</button>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
@layer surface {
  .body {
    margin: 0;
    color: var(--ink-muted);
    font-size: var(--text-sm);
    line-height: var(--leading-base);
  }
  .field { display: flex; flex-direction: column; gap: var(--space-2); }
  .field__label { font-size: var(--text-xs); font-weight: 600; color: var(--ink-muted); }
  .field__input { width: 100%; }
  .field__input[aria-invalid="true"] { border-color: var(--status-danger); }
  .field__error { font-size: var(--text-xs); color: var(--status-danger-text); }
}
</style>
